import React, { useCallback, useState } from 'react';
import { useIsMobile } from '../../../utils/useIsMobile';
import CheckboxDropdown from './CheckboxDropdown';

// '전체'·빈 값은 "필터 미적용"으로 본다 (적용 중 개수 계산용)
const isEmptyValue = (value) =>
  value === undefined ||
  value === null ||
  value === '' ||
  value === false ||
  value === '전체' ||
  (Array.isArray(value) && value.filter((v) => v !== '전체').length === 0);

const isSameFilterValue = (value, defaultValue) => {
  if (Array.isArray(value) || Array.isArray(defaultValue)) {
    const current = Array.isArray(value) ? value.filter((v) => v !== '전체') : [];
    const base = Array.isArray(defaultValue) ? defaultValue.filter((v) => v !== '전체') : [];
    return current.length === base.length && current.every((v) => base.includes(v));
  }
  if (isEmptyValue(value) && isEmptyValue(defaultValue)) return true;
  return value === defaultValue;
};

/**
 * 캠페인 필터 바 컴포넌트
 * 모바일(768px 이하)에서는 검색(text) 필드만 노출하고 나머지는 '필터' 버튼으로 펼친다.
 * @param {Object} props
 * @param {Array} props.schema - 필터 스키마 배열
 * @param {Object} props.filter - 현재 필터 상태
 * @param {Object} [props.defaults] - 필터 기본값 (모바일 '적용 중 N' 계산용)
 * @param {Function} props.onChange - 필터 변경 핸들러
 * @param {Function} props.onApply - 필터 적용 핸들러
 * @param {Function} props.onReset - 필터 초기화 핸들러
 */
function CampaignFilterBar({ schema = [], filter = {}, defaults = {}, onChange, onApply, onReset }) {
  const isMobile = useIsMobile();
  const [isExpanded, setIsExpanded] = useState(false);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      onChange(name, checked ? true : null);
    } else {
      onChange(name, value);
    }
  }, [onChange]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && onApply) {
      onApply();
    }
  }, [onApply]);

  if (!schema || schema.length === 0) {
    return null;
  }

  const renderControl = (field) => (
    <>
      {field.type === 'text' && (
        <input
          type="text"
          name={field.name}
          placeholder={field.placeholder || ''}
          value={filter[field.name] || ''}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
      )}
      {field.type === 'date' && (
        <input
          type="date"
          name={field.name}
          value={filter[field.name] || ''}
          onChange={handleChange}
        />
      )}
      {field.type === 'select' && (
        <select
          name={field.name}
          value={filter[field.name] || ''}
          onChange={handleChange}
        >
          {field.options?.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
      {field.type === 'checkbox' && (
        <input
          type="checkbox"
          name={field.name}
          checked={filter[field.name] === true}
          onChange={handleChange}
        />
      )}
      {field.type === 'multi' && (
        <CheckboxDropdown
          name={field.name}
          options={field.options || []}
          value={filter[field.name]}
          onChange={onChange}
        />
      )}
      {field.type === 'checkbox-group' && (
        <div className="checkbox-group">
          {field.options?.map((option) => (
            <label key={option.value}>
              <input
                type="checkbox"
                name={field.name}
                value={option.value}
                checked={Array.isArray(filter[field.name]) && filter[field.name].includes(option.value)}
                onChange={(e) => {
                  const current = Array.isArray(filter[field.name]) ? filter[field.name] : [];
                  const updated = e.target.checked
                    ? [...current, option.value]
                    : current.filter((v) => v !== option.value);
                  onChange(field.name, updated);
                }}
              />
              {' '}
              {option.label}
            </label>
          ))}
        </div>
      )}
    </>
  );

  if (isMobile) {
    const visibleFields = schema.filter((field) => field.visible === undefined || field.visible);
    const textFields = visibleFields.filter((field) => field.type === 'text');
    const otherFields = visibleFields.filter((field) => field.type !== 'text');
    const activeCount = otherFields.filter(
      (field) => !isSameFilterValue(filter[field.name], defaults[field.name]),
    ).length;

    return (
      <div className="campaign-filter-mobile">
        <div className="campaign-filter-mobile__top">
          {textFields.map((field) => (
            <input
              key={field.name}
              type="text"
              className="campaign-filter-mobile__search"
              name={field.name}
              placeholder={field.placeholder || field.label}
              aria-label={field.label}
              value={filter[field.name] || ''}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
            />
          ))}
          {onApply && textFields.length > 0 && (
            <button type="button" className="campaign-filter-mobile__search-btn" onClick={onApply}>
              검색
            </button>
          )}
          {otherFields.length > 0 && (
            <button
              type="button"
              className={`campaign-filter-mobile__toggle${activeCount > 0 ? ' has-active' : ''}`}
              aria-expanded={isExpanded}
              onClick={() => setIsExpanded((prev) => !prev)}
            >
              필터
              {activeCount > 0 && <span className="campaign-filter-mobile__badge">{activeCount}</span>}
            </button>
          )}
        </div>
        {isExpanded && otherFields.length > 0 && (
          <div className="campaign-filter-mobile__panel">
            {otherFields.map((field) => (
              <div key={field.name} className="campaign-filter-mobile__group">
                <label>{field.label}</label>
                {renderControl(field)}
              </div>
            ))}
            {onApply && (
              <div className="campaign-filter-mobile__actions">
                <button type="button" className="campaign-filter-mobile__apply" onClick={onApply}>
                  필터 적용
                </button>
                {onReset && (
                  <button type="button" className="campaign-filter-mobile__reset" onClick={onReset}>
                    초기화
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="filter-bar">
      {schema.map((field) => {
        // visible 조건 확인
        if (field.visible !== undefined && !field.visible) {
          return null;
        }

        return (
          <div key={field.name} className="filter-group">
            <label>{field.label}</label>
            {renderControl(field)}
          </div>
        );
      })}
      {onApply && (
        <div className="filter-group filter-apply-button">
          <button onClick={onApply}>필터 적용</button>
          {onReset && (
            <button onClick={onReset} className="reset-btn">초기화</button>
          )}
        </div>
      )}
    </div>
  );
}

export default React.memo(CampaignFilterBar);
